/** `body.tether` — an entity that rides a parent entity at an offset: a
 * beam under its emitter, a captive above its captor, a docked wingman beside
 * its ship, a turret on a hull. The parent is a spawn parameter
 * (`params.parent`, optional `params.dx`/`params.dy` override the configured
 * offset). The child hides while its parent is hidden and returns with it;
 * when the parent is gone the child is despawned (or stays where it is). */
import { bool, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str } from '../module.js';
import { confineX } from '../physics.js';

export type TetherConfig = {
  dx: number;
  dy: number;
  clampToWorld: boolean;
  orphan: 'despawn' | 'stay';
};

export const tether = defineModule<TetherConfig>({
  id: 'body.tether', version: 1, attach: 'entity', role: 'movement',
  summary: 'Rides a parent entity (spawn parameter) at an offset; hides with it; despawns or stays when the parent is gone.',
  config: obj({ dx: num(-16, 16), dy: num(-16, 16), clampToWorld: bool(), orphan: oneOf('despawn', 'stay') }),
  requires: [], events: [], presentation: {}, proofs: ['follows-parent', 'offset-override', 'hides-with-parent', 'orphan-despawns', 'orphan-stays', 'clamped'],
  includeHidden: true,
  animStates: () => [],
  init(ctx, e, params) {
    Object.assign(ctx.data(e), {
      parent: typeof params.parent === 'string' ? params.parent : '',
      dx: typeof params.dx === 'number' ? params.dx : ctx.config.dx,
      dy: typeof params.dy === 'number' ? params.dy : ctx.config.dy,
      // Spawned hidden beside a hidden parent: it reappears with it.
      mirrored: e.status === 'hidden',
    });
  },
  systems: {
    // Collide stage: after every body has moved this tick, so the child
    // never trails its parent by a frame.
    collide(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      const parent = ctx.entity(str(d, 'parent'));
      if (!parent) {
        if (c.orphan === 'despawn') ctx.despawn(e, 'orphaned');
        return;
      }
      if (parent.status === 'hidden' && e.status !== 'hidden') { e.status = 'hidden'; d.mirrored = true; }
      else if (parent.status !== 'hidden' && d.mirrored === true) { e.status = 'active'; d.mirrored = false; }
      e.x = parent.x + read(d, 'dx'); e.y = parent.y + read(d, 'dy');
      e.vx = parent.vx; e.vy = parent.vy;
      if (c.clampToWorld) confineX(ctx.world, e);
    },
  },
});
